import React, { useEffect, useState, useRef } from 'react';
import { X, RefreshCw, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import '../../styles/modals/AppPreviewModal.css';

const AppPreviewModal = ({ isOpen, onClose, groupId }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const iframeRef = useRef(null);

  // Allow body scroll when modal is open (no longer modal, just a side panel)
  useEffect(() => {
    // Removed overflow hidden to allow interaction with main content
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Reset refresh key when modal opens
  useEffect(() => {
    if (isOpen) {
      setRefreshKey(Date.now());
    }
  }, [isOpen]);

  // Handle refresh button click
  const handleRefresh = () => {
    setIsRefreshing(true);
    
    // Force a hard refresh by updating the iframe src with a new cache-busting parameter
    const newKey = Date.now();
    setRefreshKey(newKey);
    
    // Reset refreshing state after animation
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };

  // Navigation handlers
  const handleBack = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.history.back();
    }
  };

  const handleForward = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.history.forward();
    }
  };

  // Update navigation button states
  useEffect(() => {
    const updateNavigationState = () => {
      // This is a simplified check - actual implementation would need iframe communication
      // For now, we'll just enable the buttons after initial load
      setCanGoBack(true);
      setCanGoForward(false);
    };

    if (isOpen && iframeRef.current) {
      const iframe = iframeRef.current;
      iframe.addEventListener('load', updateNavigationState);
      
      return () => {
        iframe.removeEventListener('load', updateNavigationState);
      };
    }
  }, [isOpen, refreshKey]);

  if (!isOpen) return null;

  return (
    <>
      {/* Slide-in Panel (no backdrop - allows interaction with main content) */}
      <div className={`app-preview-panel ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="app-preview-header">
          <h2 className="app-preview-title">Mobile App Preview</h2>
          <div className="app-preview-header-actions">
            <button
              onClick={handleBack}
              className="app-preview-nav-btn"
              aria-label="Go back"
              title="Go back"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleForward}
              className="app-preview-nav-btn"
              aria-label="Go forward"
              title="Go forward"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleRefresh}
              className="app-preview-refresh-btn"
              aria-label="Refresh preview"
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={onClose}
              className="app-preview-close-btn"
              aria-label="Close preview"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Phone Frame Container */}
        <div className="app-preview-content">
          <div className="app-preview-phone-frame">
            <div className="app-preview-phone-screen">
              <iframe
                ref={iframeRef}
                key={refreshKey}
                src={`${window.location.origin}/#/community/${groupId}?_refresh=${refreshKey}`}
                className="app-preview-iframe"
                title="App Preview"
                sandbox="allow-same-origin allow-scripts allow-popups"
              />
            </div>
            
            {/* Phone Details */}
            <div className="app-preview-phone-details">
              <div className="app-preview-phone-speaker"></div>
              <div className="app-preview-phone-home-indicator"></div>
            </div>
          </div>
        </div>
        
        {/* View Web Page Link - Below iframe */}
        <div className="app-preview-footer">
          <a
            href={`${window.location.origin}/#/community/${groupId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="app-preview-web-link"
          >
            <ExternalLink className="w-4 h-4" />
            <span>View Web Page</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default AppPreviewModal;
